import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ObjectID } from "bson";
import { buildFetcher, buildSetterWithInstanceIdProvided } from "src/api";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";
import { CapacityData, CapacityTimelineData } from "../timesheets/timesheets";
import { useAccessToken } from "../useAccessToken";

export const settingsKeys = {
  settings: () => ["settings", "all"] as const,
  instanceSettings: (instanceId: string) => ["settings", instanceId] as const,
  userSettings: (userId: string) => ["settings", "user", userId] as const,
  workingTimeSettings: (projectId: string) =>
    ["settings", "workingTime", projectId] as const,
};

// TYPES

export type SettingsData = {
  _id: ObjectID;
  timeTracking: {
    lockSubmittedEntries: boolean;
    defaultCapacities: CapacityData[];
    defaultCapacityTimeline: CapacityTimelineData[];
    isExternallyVisible: boolean;
  };
  apps: {
    timeTracking: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
    resourceManagement: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
    billing: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
    lms: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
  };
  instanceId: ObjectID;
  services: {
    tasks?: boolean;
    sharedSpaces?: boolean;
    feedback?: boolean;
    knowledgeBase?: boolean;
    customerHealth?: boolean;
  };
  view: {
    // tabs: {
    //   dashboard?: boolean;
    //   tasks?: boolean;
    //   journeys?: boolean;
    //   sharedSpaces?: boolean;
    //   feedback?: boolean;
    //   knowledgeBase?: boolean;
    //   customerHealth?: boolean;
    //   customers?: boolean;
    //   threads?: boolean;
    //   forms?: boolean;
    //   support?: boolean;
    // },
    integrations: {
      slack: boolean;
      crm: boolean;
    };
    tasks: {
      new: {
        estimates: boolean;
        dueTime: boolean;
      };
    };
  };
  embed: {
    primaryColor?: string;
    secondaryColor?: string;
    font?: string;
    fontType?: "sans" | "serif" | "mono";
  };
  createdAt: Date;
  updatedAt: Date;
  timezone: string;
  workingTimeDetails: {
    startTime: string; //"HH:MM",
    workingTimePerDay: number; //in ms
    days: {
      0: boolean;
      1: boolean;
      2: boolean;
      3: boolean;
      4: boolean;
      5: boolean;
      6: boolean;
    };
  };
};

export type SettingsRequest = {
  apps?: {
    timeTracking?: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
    resourceManagement?: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
    billing?: {
      isAccessible: boolean;
      isEnabled: boolean;
    };
  };
  timeTracking?: {
    isExternallyVisible?: boolean;
  };
  // _id: ObjectID;
  // instanceId: ObjectID;
  services?: {
    tasks?: boolean;
    sharedSpaces?: boolean;
    feedback?: boolean;
    knowledgeBase?: boolean;
    customerHealth?: boolean;
  };
  view?: {
    tabs?: {
      dashboard?: boolean;
      tasks?: boolean;
      journeys?: boolean;
      sharedSpaces?: boolean;
      feedback?: boolean;
      knowledgeBase?: boolean;
      customerHealth?: boolean;
      customers?: boolean;
      threads?: boolean;
      forms?: boolean;
      support?: boolean;
    };
    tasks?: {
      new?: {
        estimates?: boolean;
      };
    };
  };
  embed?: {
    primaryColor?: string;
    secondaryColor?: string;
    font?: string;
    fontType?: "sans" | "serif" | "mono";
  };
};

// TEMPLATES

export const useGetSettings = () => {
  const app = useRealmApp();
  const meta = {
    functionName: "getInstanceSettings",
    parameters: {
      instanceId: app.currentUser.customData.instanceId,
    },
  };

  return useQuery<SettingsData>(
    settingsKeys.settings(),
    buildFetcher(app, meta),
    {
      staleTime: Infinity,
    }
  );
};

export type InstanceWorkingTimeSettings = {
  workingTimeDetails: SettingsData["workingTimeDetails"];
  timezone: string;
}
export const useGetInstanceWorkingTimeSettings = (projectId: string) => {
  const getValidAccessToken = useAccessToken();
  return useQuery(
    settingsKeys.workingTimeSettings(projectId),
    async (): Promise<InstanceWorkingTimeSettings> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        // `https://fb8xf9wmy6.execute-api.us-east-1.amazonaws.com/development/feedback/getAll`,
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/instanceWorkingTimeSettings`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            projectId,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );
};

export const useGetSettingsForEmbeddedSharedSpace = (
  sharedSpaceId: string
): {
  settings: SettingsData | undefined;
} => {
  const app = useRealmApp();
  const meta = {
    functionName: "getInstanceSettings",
    parameters: {
      sharedSpaceId: new ObjectID(sharedSpaceId),
    },
  };

  const { data } = useQuery<SettingsData>(
    settingsKeys.settings(),
    buildFetcher(app, meta),
    {
      staleTime: Infinity,
    }
  );

  // console.log("Fetching settings");
  // if (data) console.log(data);

  return {
    settings: data,
  };
};

export const useGetInstanceSettingsFromSlug = (slug: string) => {
  const app = useRealmApp();
  const meta = {
    functionName: "getInstanceSettings",
    parameters: {
      slug,
    },
  };

  return useQuery<SettingsData>(
    settingsKeys.settings(),
    buildFetcher(app, meta),
    {
      staleTime: Infinity,
    }
  );
};

export const useGetInstanceSettings = () => {
  const app = useRealmApp();
  const meta = {
    functionName: "getInstanceSettings",
    parameters: {
      instanceId: app.currentUser.customData.instanceId,
    },
  };

  return useQuery<SettingsData>(
    settingsKeys.instanceSettings(app.currentUser.customData.instanceId.$oid),
    buildFetcher(app, meta),
    {
      staleTime: Infinity,
    }
  );
};

export const useUpdateSettings = () => {
  const app = useRealmApp();
  const functionName = "updateInstanceSettings";
  const fieldName = "update";

  const queryClient = useQueryClient();
  const snackbarCtx = useSnackBar();

  const updateSettingsMutation = useMutation(
    buildSetterWithInstanceIdProvided(app, functionName, fieldName),
    {
      onMutate: async ({ update }: { update: SettingsRequest }) => {
        // const queryKey = settingsKeys.settings();
        // await queryClient.cancelQueries(queryKey);
        // const previousSettings = queryClient.getQueryData<SettingsData>(queryKey);
        // const newSettings = previousSettings
        //   ? previousSettings.map((t) =>
        //       t._id?.toString() === update._id?.toString()
        //         ? { ...t, ...update }
        //         : t
        //     )
        //   : [];
        // queryClient.setQueryData(queryKey, newTags);
        // return { previousTags };
      },
      onError: (data, variables, context) => {
        snackbarCtx.showSnackbar("Error updating the settings", "error");
        // if (variables.type)
        //   queryClient.setQueryData(
        //     tagKeys.tags(variables.type),
        //     context?.previousTags
        //   );
      },
      onSuccess: (data, error, variables) => {
        // if (!variables.update.view)
        //   snackbarCtx.showSnackbar("New settings saved", "success");
        queryClient.invalidateQueries(settingsKeys.settings());
        // if (variables.type)
        //   queryClient.invalidateQueries(tagKeys.tags(variables.type));
      },
    }
  );

  return updateSettingsMutation;
};

export type UserSettingsData = {
  userId: string;
  timeTracking: {
    weeklyCapacity: number;
    hourlyRate: {
      value: number;
      currency: string;
    };
  };
  calendarIntegration?: {
    integrationId: "google-calendar";
    oAuth: boolean;
  };
  createdAt: string;
  updatedAt: string;
};

export const useGetUserSettings = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  // console.log(app.currentUser.id)
  return useQuery(
    settingsKeys.userSettings(app.currentUser.id),
    async (): Promise<UserSettingsData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/userSettings`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          // params: {
          //   instanceId: app.currentUser.customData.instanceId.$oid,
          // },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};
